import React, { useContext, useEffect } from "react";
import { Link, useLocation, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { createPageUrl } from "./utils/utils";
import { Gift, Users, Upload, LayoutDashboard, ListChecks, LogOut, History, Layers, Truck, User, Package, Menu, X, Settings, Nfc, ChevronDown, ChevronUp, Plus, FileText, Video } from "lucide-react";
import { Sidebar } from "./Components/ui/sidebar";
import Footer from "./Components/Footer";
import HomePage from "./Pages/home";
import AdminDashboard from "./Pages/AdminDashboard";
import DonorDashboard from "./Pages/DonorDashboard";
import ImpactHighlights from "./Pages/ImpactHighlights";
import DonorsList from "./Pages/DonorsList";
import BatchStaffList from "./Pages/BatchStaffList";
import CreateDonation from "./Pages/CreateDonation";
import CreateBatch from "./Pages/CreateBatch";
import ListBatches from "./Pages/ListBatches";
import DonationDetails from "./Pages/DonationDetails";
import Login from "./Pages/Login";
import SignUp from "./Pages/SignUp";
import Profile from "./Pages/Profile";
import Payment from "./Pages/Payment";
import DonorDetailsAdmin from "./Pages/DonorDetailsAdmin";
import StaffDetailsAdmin from "./Pages/StaffDetailsAdmin";
import AdminLinkNFC from "./Pages/AdminLinkNFC";
import { AuthContext } from "./context/authContext";
import { ThemeContext } from "./context/ThemeContext";
import DonationRequests from "./Pages/DonationRequests";
import DonationHistory from "./Pages/DonationHistory";
import Pools from "./Pages/Pools";
import ThemeToggle from "./Components/ui/ThemeToggle";
import NotificationDropdown from "./Components/ui/NotificationDropdown";

import { Button } from "./Components/ui/button";


import LogoutModal from "./Components/LogoutModal";
import MandatoryUpdateModal from "./Components/MandatoryUpdateModal";

import AdminSettingsPanel from "./Components/AdminSettingsPanel";

const donorNavigationItems = [
  { title: "Donor Dashboard", url: createPageUrl("donordashboard"), icon: LayoutDashboard, component: DonorDashboard },
  { title: "Impact Highlights", url: createPageUrl("impact-highlights"), icon: Video, component: ImpactHighlights },
  { title: "Create Donation", url: createPageUrl("createdonation"), icon: Gift, component: CreateDonation },
  { title: "Donation History", url: createPageUrl("donation-history"), icon: History, component: DonationHistory },
  { title: "Profile", url: createPageUrl("profile"), icon: User, component: Profile },
];



const adminNavigationItems = [
  { title: "Admin Dashboard", url: createPageUrl("admin-dashboard"), icon: LayoutDashboard, component: AdminDashboard },
  {
    title: "Donation Management", icon: Gift, subItems: [
      { title: "Donation Requests", url: createPageUrl("donation-requests"), icon: ListChecks },
      { title: "Donation History", url: createPageUrl("donation-history"), icon: History },
      { title: "Pools", url: createPageUrl("pools"), icon: Layers },
      { title: "Donors List", url: createPageUrl("donorslist"), icon: Users },
      { title: "Link NFC Tags", url: createPageUrl("admin-link-nfc"), icon: Nfc }
    ]
  },
  {
    title: "Delivery Management", icon: Package, subItems: [
      { title: "Create Batch", url: createPageUrl("create-batch"), icon: Plus },
      { title: "List of all batches", url: createPageUrl("list-batches"), icon: FileText },
      { title: "Logistics Team", url: createPageUrl("batchstafflist"), icon: Truck }
    ]
  },
  { title: "Settings", icon: Settings, action: "openSettings" }, // New Settings Item
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);
  const [showMandatoryModal, setShowMandatoryModal] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false); // Global Settings State
  const [expandedMenus, setExpandedMenus] = React.useState({});

  const toggleSubMenu = (title) => {
    setExpandedMenus(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const logoSrc = theme === 'dark' ? '/assets/images/logo-dark.png' : '/assets/images/logo-light.png';

  useEffect(() => {
    // Check for mandatory updates
    if (user && user.role !== 'Batch staff') {
      const needsEmail = !user.email || !user.isEmailVerified;
      const needsPhone = !user.phone_number || !user.isPhoneVerified;

      if (needsEmail || needsPhone) {
        setShowMandatoryModal(true);
      } else {
        setShowMandatoryModal(false);
      }
    } else {
      setShowMandatoryModal(false);
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (user && user.role === 'Batch staff') {
      logout();
      navigate('/login', { state: { error: "Use the app to login" } });
      return;
    }

    const authPages = ['/login', '/signup', '/', '/home'];
    if (user && authPages.includes(location.pathname)) {
      const role = user.role;
      if (role === 'Administrator') {
        navigate('/admin-dashboard');
      } else {
        navigate('/donordashboard');
      }
    }
  }, [user, navigate, location.pathname, logout]);

  const handleLogoutConfirm = () => {
    logout();
    setIsLogoutModalOpen(false);
    navigate('/home');
  };

  const handleItemClick = (item) => {
    if (item.action === "openSettings") {
      setIsSettingsOpen(true);
      setIsMobileMenuOpen(false);
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  // Determine if the sidebar should be shown based on the current route
  const noSidebarRoutes = ["/home", "/", "/login", "/signup"];
  const showSidebar = user && !noSidebarRoutes.includes(location.pathname);

  // Determine which navigation items to show based on user role
  let sidebarItems = [];
  if (user) {
    if (user.role === 'Administrator') {
      sidebarItems = adminNavigationItems;
    } else if (user.role === 'Donor') {
      sidebarItems = donorNavigationItems;
    }
  }

  // Determine if we are on the home page for transparency
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  return (
    <div className={`min-h-screen w-full ${showSidebar ? 'flex' : ''} bg-zinc-50 dark:bg-zinc-950 transition-colors duration-300`}>
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
      />

      {/* Global Admin Settings Panel */}
      {user?.role === 'Administrator' && (
        <AdminSettingsPanel open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
      )}

      {showMandatoryModal && user && user.role !== 'Batch staff' && (
        <MandatoryUpdateModal
          user={user}
          onComplete={() => window.location.reload()}
        />
      )}
      <div className={`${!showSidebar ? 'bg-zinc-50 dark:bg-zinc-950' : ''} absolute inset-0 -z-10 transition-colors duration-300`} />
      {showSidebar ? (
        <>
          {/* Desktop Sidebar */}
          <Sidebar className="hidden md:flex border-r border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md flex-col h-screen overflow-y-auto transition-colors duration-300 w-64 fixed left-0 top-0 bottom-0 z-40">

            <div className="pt-6 pb-2">
              <div className="flex items-center justify-center gap-3">
                <img src={logoSrc} alt="Say Whaat Logo" className="h-24 w-auto object-contain" />
              </div>
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div className="p-3 pt-0">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider px-3 py-1">
                  Navigation
                </p>
                <div>
                  <div>
                    {sidebarItems.map((item) => (
                      <div key={item.title} className="mb-1">
                        {item.subItems ? (
                          <>
                            <button
                              onClick={() => toggleSubMenu(item.title)}
                              className="w-full transition-colors flex items-center justify-between p-3 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300 text-left"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <item.icon className="w-5 h-5 shrink-0 text-zinc-500" />
                                <span className="font-medium leading-tight">{item.title}</span>
                              </div>
                              {expandedMenus[item.title] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {expandedMenus[item.title] && (
                              <div className="ml-4 mt-1 space-y-1 border-l border-zinc-200 dark:border-zinc-800 pl-2">
                                {item.subItems.map((subItem) => (
                                  <Link key={subItem.title} to={subItem.url} className={`transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-2 text-sm ${(location.pathname === subItem.url)
                                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-200 dark:border-zinc-700'
                                    : 'text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100'
                                    }`}>
                                    <subItem.icon className="w-4 h-4" />
                                    <span className="font-medium">{subItem.title}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        ) : item.url ? (
                          <Link to={item.url} className={`transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-3 ${(location.pathname === item.url || (location.pathname === '/' && item.url === '/home'))
                            ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-200 dark:border-zinc-700'
                            : 'text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100'
                            }`}>
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.title}</span>
                          </Link>
                        ) : (
                          <button
                            onClick={() => handleItemClick(item)}
                            className="w-full transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-3 text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100 cursor-pointer text-left"
                          >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.title}</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </Sidebar>

          {/* Mobile Sidebar Overlay */}
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Mobile Sidebar Drawer */}
          <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-zinc-900 shadow-xl transform transition-transform duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-800">
              <img src={logoSrc} alt="Say Whaat Logo" className="h-8 w-auto" />
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto h-[calc(100vh-64px)]">
              <p className="text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider px-3 py-2">
                Navigation
              </p>
              {sidebarItems.map((item) => (
                <div key={item.title} className="mb-1">
                  {item.subItems ? (
                    <>
                      <button
                        onClick={() => toggleSubMenu(item.title)}
                        className={`w-full transition-all duration-200 rounded-xl flex items-center justify-between px-4 py-3 text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100 cursor-pointer`}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.title}</span>
                        </div>
                        {expandedMenus[item.title] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {expandedMenus[item.title] && (
                        <div className="ml-4 mt-1 space-y-1 border-l border-zinc-200 dark:border-zinc-800 pl-2">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.title}
                              to={subItem.url}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-2 text-sm ${(location.pathname === subItem.url)
                                ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-200 dark:border-zinc-700'
                                : 'text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100'
                                }`}>
                              <subItem.icon className="w-4 h-4" />
                              <span className="font-medium">{subItem.title}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : item.url ? (
                    <Link
                      to={item.url}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-3 ${(location.pathname === item.url || (location.pathname === '/' && item.url === '/home'))
                        ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-200 dark:border-zinc-700'
                        : 'text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100'
                        }`}>
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.title}</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleItemClick(item)}
                      className="w-full transition-all duration-200 rounded-xl flex items-center gap-3 px-4 py-3 text-zinc-500 dark:text-400 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100 cursor-pointer text-left">
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.title}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (

        <header className={`w-full z-50 transition-colors duration-300 ${isHomePage
          ? "absolute top-0 bg-transparent border-transparent"
          : "sticky top-0 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800"
          }`}>
          <div className="container mx-auto px-6 py-3 flex justify-between items-center">
            {/* Hide logo on home page as it is displayed in the hero section */}
            {!isHomePage ? (
              <Link to={createPageUrl("home")} className="flex items-center gap-3">
                <img src={logoSrc} alt="Say Whaat Logo" className="h-10 w-auto object-contain" />
              </Link>
            ) : (
              <div /> // Spacer to keep flex layout working if needed, or just nothing
            )}
            <div className="flex items-center gap-4">
              {user && <NotificationDropdown align="right" />}
              <ThemeToggle />
              {isHomePage && !user && (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="font-semibold px-4 py-2 text-md border border-white/50 text-white hover:bg-white/20 hover:text-white bg-transparent backdrop-blur-sm">Login</Button>
                  </Link>
                  <Link to="/signup">
                    <Button variant="ghost" className="font-semibold !bg-white !text-black hover:!bg-zinc-200 px-4 py-2 text-md shadow-lg rounded-md">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
              {isHomePage && user && (
                <Link to={
                  user.role === 'Administrator' ? "/admin-dashboard" : "/donordashboard"
                }>
                  <Button variant="ghost" className="font-semibold px-4 py-2 text-md border border-white/50 text-white hover:bg-white/20 hover:text-white bg-transparent backdrop-blur-sm">
                    Dashboard
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </header>
      )}

      <main className="flex-1 flex flex-col relative">
        {showSidebar && (
          <header className={`sticky top-0 z-30 flex ${isMobileMenuOpen ? 'z-30' : 'z-30'} justify-between md:justify-end items-center px-6 py-4 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 ml-0 md:ml-64 transition-all duration-300`}>
            {/* Mobile Menu Toggle */}
            <div className="md:hidden flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)}>
                <Menu className="w-5 h-5" />
              </Button>
              <Link to={createPageUrl("home")} className="flex items-center">
                <img src={logoSrc} alt="Say Whaat Logo" className="h-8 w-auto object-contain" />
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <NotificationDropdown align="right" />
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition-all"
                title="Logout"
                onClick={() => setIsLogoutModalOpen(true)}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </header>
        )}
        <div className={`flex-1 overflow-auto flex flex-col relative ${showSidebar ? 'ml-0 md:ml-64' : ''} transition-all duration-300`}>
          <div className="flex-1">

            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />

              {/* Dashboards - Strict Role Checks */}
              <Route path="/donordashboard" element={user && (user.role === 'Donor' || !user.role) ? <DonorDashboard /> : <Navigate to="/login" replace />} />
              <Route path="/impact-highlights" element={user && (user.role === 'Donor' || !user.role) ? <ImpactHighlights /> : <Navigate to="/login" replace />} />
              <Route path="/admin-dashboard" element={user && user.role === 'Administrator' ? <AdminDashboard /> : <Navigate to="/login" replace />} />

              {/* Admin Routes */}
              <Route path="/donation-requests" element={user && user.role === 'Administrator' ? <DonationRequests /> : <Navigate to="/login" replace />} />
              <Route path="/donorslist" element={user && user.role === 'Administrator' ? <DonorsList /> : <Navigate to="/login" replace />} />
              <Route path="/admin/donors/:id" element={user && user.role === 'Administrator' ? <DonorDetailsAdmin /> : <Navigate to="/login" replace />} />
              <Route path="/batchstafflist" element={user && user.role === 'Administrator' ? <BatchStaffList /> : <Navigate to="/login" replace />} />
              <Route path="/create-batch" element={user && user.role === 'Administrator' ? <CreateBatch /> : <Navigate to="/login" replace />} />
              <Route path="/list-batches" element={user && user.role === 'Administrator' ? <ListBatches /> : <Navigate to="/login" replace />} />
              <Route path="/admin/staff/:id" element={user && user.role === 'Administrator' ? <StaffDetailsAdmin /> : <Navigate to="/login" replace />} />
              <Route path="/admin-link-nfc" element={user && user.role === 'Administrator' ? <AdminLinkNFC /> : <Navigate to="/login" replace />} />
              <Route path="/pools" element={user && user.role === 'Administrator' ? <Pools /> : <Navigate to="/login" replace />} />



              {/* Shared/Common Routes */}
              <Route path="/donation-history" element={user ? <DonationHistory /> : <Navigate to="/login" replace />} />
              <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" replace />} />
              <Route path="/createdonation" element={user && user.role === 'Donor' ? <CreateDonation /> : <Navigate to="/login" replace />} />
              <Route path="/donations/:id" element={user ? <DonationDetails /> : <Navigate to="/login" replace />} />
              <Route path="/payment" element={user ? <Payment /> : <Navigate to="/login" replace />} />

              <Route path="/" element={<HomePage />} />
              <Route path="/home" element={<HomePage />} />
            </Routes>

          </div>
          {isHomePage && <Footer />}
        </div>
      </main >
    </div >
  );
}
